import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import {
    GroupWork as GroupWorkIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    LocationOn as LocationOnIcon,
    Info as InfoIcon,
    HowToReg as HowToRegIcon,
} from '@mui/icons-material';
import moment from 'moment';
import Teachr from 'src/Objects/models/Teachr';
import { capitalizeFirstLetter, createTeachrAddress, prepareTeachrForUpdate, updateTeachrData } from 'src/Utils/Function';
import { useProfilContext } from '../../../../Contexts/ProfilContexte';
import { userSetCooperativeForm } from '../../../../Redux/Actions/UserActions';
import ButtonField from '../../../../Components/Ui/ButtonField';
import Settings from '../../../../Components/Settings';
import FormInput from '../../../../Components/Ui/FormInput';
import FormDate from '../../../../Components/FormFields/FormDate';
import AddressDetailed, { AddressFields, AddressErrors } from '../../../../Components/Ui/AddressDetailed';
import DialogLayout from '../../../../Components/DialogLayout';
import useTailwindBrand from '../../../../Theme/useTailwindBrand';
import { isBrandActive, ENV_VERIFIED_TRAINER, getActiveAppBrandConfig } from '../../../../config/environment.config';

interface CoopForm {
    nom: string;
    prenom: string;
    dateNaissance: string;
    villeNaissance: string;
    denominationSociale: string;
    immatriculeRCS: string;
    siren: string;
    email: string;
    telephone: string;
    code: string;
}

type CoopErrors = Partial<Record<keyof CoopForm, string>>;

const validateCoopForm = (form: CoopForm, isVerifiedTrainer: boolean): CoopErrors => {
    const errors: CoopErrors = {};
    if (!form.nom.trim()) errors.nom = 'Le nom est requis.';
    if (!form.prenom.trim()) errors.prenom = 'Le prénom est requis.';
    if (!isVerifiedTrainer) {
        if (!form.dateNaissance.trim()) errors.dateNaissance = 'La date de naissance est requise.';
        if (!form.villeNaissance.trim()) errors.villeNaissance = 'La ville de naissance est requise.';
    }
    if (!form.denominationSociale.trim()) errors.denominationSociale = 'La dénomination sociale est requise.';
    if (!/^\d{9}$/.test(form.siren.trim())) errors.siren = 'Le SIREN doit contenir exactement 9 chiffres.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Adresse email invalide.';
    if (!/^(\+33|0)[1-9](\d{2}){4}$/.test(form.telephone.replace(/\s/g, ''))) errors.telephone = 'Numéro de téléphone invalide.';
    return errors;
};

const validateAddressFields = (addr: AddressFields): AddressErrors => {
    const errors: AddressErrors = {};
    if (!addr.libelleVoie.trim()) errors.libelleVoie = 'Le libellé de voie est requis.';
    if (!addr.codePostal.trim() || !/^\d{5}$/.test(addr.codePostal.trim())) errors.codePostal = 'Code postal invalide (5 chiffres requis).';
    if (!addr.ville.trim()) errors.ville = 'La ville est requise.';
    return errors;
};

interface CooperativeFormProps {
    onPrevious: () => void;
}

const CooperativeForm: React.FC<CooperativeFormProps> = () => {
    const { teachr, nextStep, setState, formData: cooperativeForm, handleRefreshSiret } = useProfilContext();
    const dispatch = useDispatch();
    const { brandConfig } = useTailwindBrand();
    const siretInfo = useSelector((state: any) => state.user?.siretInfo?.etablissement);

    // const cooperativeForm = useSelector((state: any) => state.user?.cooperativeFormData);
    const primaryHex = brandConfig.colors?.primary ?? '#1976d2';
    const isVerifiedTrainer = isBrandActive(ENV_VERIFIED_TRAINER);
    const appName = getActiveAppBrandConfig();

    useEffect(() => { handleRefreshSiret && handleRefreshSiret() }, [handleRefreshSiret]); 
    useEffect(() => {
            if (!siretInfo) return;
            setForm(prev => ({
                ...prev,
                denominationSociale: prev.denominationSociale || (siretInfo?.uniteLegale?.denominationUniteLegale ? siretInfo?.uniteLegale?.denominationUniteLegale : capitalizeFirstLetter(teachr?.lastName) + ' ' + capitalizeFirstLetter(teachr?.firstName)) || '',
                siren: (teachr?.RCS)?.slice(0, 9) || siretInfo?.siren || '',
            }));
            setAddressFields(prev => ({
                ...prev,
                numeroVoie: prev.numeroVoie || siretInfo?.adresseEtablissement?.numeroVoieEtablissement || '',
                lettreVoie: prev.lettreVoie || siretInfo?.adresseEtablissement?.indiceRepetitionEtablissement || '',
                typeVoie: prev.typeVoie || siretInfo?.adresseEtablissement?.typeVoieEtablissement || '',
                libelleVoie: prev.libelleVoie || siretInfo?.adresseEtablissement?.libelleVoieEtablissement || '',
                lieuDit: prev.lieuDit || siretInfo?.adresseEtablissement?.lieuDitEtablissement || '',
                complement: prev.complement || siretInfo?.adresseEtablissement?.complementEtablissement || '',
                codePostal: prev.codePostal || siretInfo?.adresseEtablissement?.codePostalEtablissement || '',
                ville: prev.ville || siretInfo?.adresseEtablissement?.libelleCommuneEtablissement || '',
            }));
        }, [siretInfo, teachr?.RCS, teachr?.firstName, teachr?.lastName]);

    const [form, setForm] = useState<CoopForm>({
        nom: capitalizeFirstLetter(cooperativeForm?.nom) || capitalizeFirstLetter(teachr?.lastName) || '',
        prenom: capitalizeFirstLetter(cooperativeForm?.prenom) || capitalizeFirstLetter(teachr?.firstName) || '',
        dateNaissance: cooperativeForm?.dateNaissance || (teachr?.birthday ? moment(teachr.birthday).format('YYYY-MM-DD') : ''),
        villeNaissance: cooperativeForm?.villeNaissance || teachr?.bornPlace || '',
        denominationSociale: capitalizeFirstLetter(cooperativeForm?.denominationSociale) || (siretInfo?.uniteLegale?.denominationUniteLegale ? siretInfo?.uniteLegale?.denominationUniteLegale : capitalizeFirstLetter(teachr?.lastName) + ' ' + capitalizeFirstLetter(teachr?.firstName)) || '',
        immatriculeRCS: cooperativeForm?.immatriculeRCS || '',
        siren: (teachr?.RCS)?.slice(0, 9) ||siretInfo?.siren || '',
        email: cooperativeForm?.email || teachr?.mail || '',
        telephone: cooperativeForm?.telephone || teachr?.phone || '',
        code: (cooperativeForm?.code ? cooperativeForm?.code : teachr?.sponsorship) || '',
    });
    const [errors, setErrors] = useState<CoopErrors>({});

    const [addressFields, setAddressFields] = useState<AddressFields>({
        numeroVoie: teachr?.address?.streetNumber || capitalizeFirstLetter(siretInfo?.adresseEtablissement?.numeroVoieEtablissement) || '',
        lettreVoie:  capitalizeFirstLetter(siretInfo?.adresseEtablissement?.indiceRepetitionEtablissement) || '',
        typeVoie: capitalizeFirstLetter(siretInfo?.adresseEtablissement?.typeVoieEtablissement) || '',
        libelleVoie: teachr?.address?.streetName || capitalizeFirstLetter(siretInfo?.adresseEtablissement?.libelleVoieEtablissement) || '',
        lieuDit: capitalizeFirstLetter(siretInfo?.adresseEtablissement?.lieuDitEtablissement) || '',
        complement: capitalizeFirstLetter(siretInfo?.adresseEtablissement?.complementEtablissement) || '',
        codePostal: teachr?.address?.zip ||  capitalizeFirstLetter(siretInfo?.adresseEtablissement?.codePostalEtablissement) || '',
        ville: teachr?.address?.city || capitalizeFirstLetter(siretInfo?.adresseEtablissement?.libelleCommuneEtablissement) || '',
    });
    const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleChange = (field: keyof CoopForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
        if (submitted) {
            setErrors(prev => ({ ...prev, [field]: validateCoopForm({ ...form, [field]: value }, isVerifiedTrainer)[field] }));
        }
    };

    const handleDateNaissance = (value: any) => {
        let dateValue = '';
        if (value) {
            if (typeof value === 'object' && value.format) dateValue = value.format('YYYY-MM-DD');
            else if (typeof value === 'string') dateValue = value;
            else if (value instanceof Date) dateValue = moment(value).format('YYYY-MM-DD');
        }
        if (dateValue) {
            setForm(prev => ({ ...prev, dateNaissance: dateValue }));
            if (submitted) setErrors(prev => ({ ...prev, dateNaissance: undefined }));
        }
    };

    const handleAddressChange = (fields: AddressFields) => {
        setAddressFields(fields);
        if (submitted) setAddressErrors(validateAddressFields(fields));
    };
    const handleCreateAddress = async (): Promise<string | null> => {
        if (teachr && teachr.id) {
            try {
                setLoading(true);
                const updatedTeachr = { ...teachr } as Teachr;

                const addressIri = (addressFields && teachr.id && teachr['@id'])
                    ? await createTeachrAddress(addressFields, teachr['@id'] as string, teachr.id)
                    : null;

                if (addressIri) updatedTeachr.address = addressIri;
                if(form.villeNaissance) updatedTeachr.bornPlace = form.villeNaissance;
              
                prepareTeachrForUpdate(updatedTeachr, {});
                await updateTeachrData(updatedTeachr, dispatch, setLoading);
                nextStep(3);
            } catch (err) {
                console.warn('Erreur lors de l\'upload des documents:', err);
                setLoading(false);
            } finally {
                setLoading(false);
            }
        }
        return null;
    }
    const handleUpdate = async () => {
        if (teachr && teachr?.address) {
            if (teachr.address?.streetNumber !== addressFields.numeroVoie ||
                teachr.address?.streetName !== addressFields.libelleVoie ||
                teachr.address?.postalCode !== addressFields.codePostal ||
                teachr.address?.city !== addressFields.ville) {
                handleCreateAddress();
            } else {
                nextStep(3);
            }
        } else {
            handleCreateAddress();
        }
    }

    const handleClickContinue = () => {
        setSubmitted(true);
        const newErrors = validateCoopForm(form, isVerifiedTrainer);
        const newAddressErrors = validateAddressFields(addressFields);
        setErrors(newErrors);
        setAddressErrors(newAddressErrors);
        if (Object.keys(newErrors).length === 0 && Object.keys(newAddressErrors).length === 0) {
            setConfirmOpen(true);
        }
    };

    const handleSubmit = () => {
        setConfirmOpen(false);
        dispatch(userSetCooperativeForm({ ...form, address: addressFields }));
        setState(prev => ({ ...prev, hasCooperative: true }));
        handleUpdate();
    };

    const sectionBoxSx = {
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        bgcolor: 'grey.50',
    };

    const sectionHeaderSx = {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2.5,
    };

    const title = isVerifiedTrainer
        ? 'Adhésion à la coopérative'
        : `Bulletin d'adhésion ${appName}`;

    const subtitle = isVerifiedTrainer
        ? 'Renseignez vos informations pour rejoindre Verified Trainer SAP'
        : 'Renseignez vos informations pour finaliser votre inscription';

    const bannerTitle = isVerifiedTrainer
        ?` 'Pourquoi adhérer à notre coopérative ${isBrandActive(ENV_VERIFIED_TRAINER) ? '' : 'partenaire'}? `
        : `Pourquoi rejoindre ${appName} ?`;

    const bannerBody = isVerifiedTrainer
        ? <>En rejoignant <strong>Verified Trainer SAP</strong>, vous pouvez facturer l'ensemble de vos prestations sans contrainte de code APE et bénéficier d'un accompagnement personnalisé pour développer votre activité en toute sérénité.</>
        : <>En rejoignant <strong>{appName}</strong>, bénéficiez d'un accompagnement personnalisé et d'outils adaptés pour développer votre activité sereinement.</>;

    return (
        <Settings
            title={title}
            subtitle={subtitle}
            icon={<GroupWorkIcon sx={{ color: primaryHex, fontSize: 28 }} />}
            height="100%"
        >
            {/* Bannière d'information */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: `${primaryHex}40`,
                        bgcolor: `${primaryHex}0d`,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5,
                    }}
                >
                    <InfoIcon sx={{ color: primaryHex, fontSize: 22, flexShrink: 0, mt: 0.25 }} />
                    <Box>
                        <Typography variant="subtitle2" fontWeight="700" sx={{ color: primaryHex, fontFamily: brandConfig.fontFamily, mb: 0.5 }}>
                            {bannerTitle}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: brandConfig.fontFamily }}>
                            {bannerBody}
                        </Typography>
                    </Box>
                </Box>
            </Grid>

            {/* Section informations de la société */}
            <Grid item xs={12}>
                <Box sx={sectionBoxSx}>
                    <Box sx={sectionHeaderSx}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: `${primaryHex}1a`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <BusinessIcon sx={{ color: primaryHex, fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight="700" fontFamily={brandConfig.fontFamily} color="text.primary">
                                Informations de la société
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily={brandConfig.fontFamily}>
                                Données de votre entreprise issues du SIRET
                            </Typography>
                        </Box>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={!isBrandActive(ENV_VERIFIED_TRAINER) ? 6 : 12}>
                            <FormInput
                                label="Dénomination sociale"
                                type="text"
                                value={form.denominationSociale}
                                onChange={handleChange('denominationSociale')}
                                error={errors.denominationSociale}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="SIREN"
                                type="text"
                                placeholder="9 chiffres"
                                value={form.siren}
                                onChange={handleChange('siren')}
                                error={errors.siren}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} className={isBrandActive(ENV_VERIFIED_TRAINER) ? "" : "hidden"}>
                            <FormInput
                                label="Immatriculé au RCS (facultatif)"
                                type="text"
                                placeholder="Ex : Paris B 123 456 789"
                                value={form.immatriculeRCS}
                                onChange={handleChange('immatriculeRCS')}
                                error={errors.immatriculeRCS}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Grid>

            {/* Section informations personnelles */}
            <Grid item xs={12}>
                <Box sx={sectionBoxSx}>
                    <Box sx={sectionHeaderSx}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: '#e0f2fe',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <PersonIcon sx={{ color: '#0284c7', fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight="700" fontFamily={brandConfig.fontFamily} color="text.primary">
                                Informations personnelles
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily={brandConfig.fontFamily}>
                                Vos coordonnées de contact
                            </Typography>
                        </Box>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Nom"
                                type="text"
                                value={form.nom}
                                onChange={handleChange('nom')}
                                error={errors.nom}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Prénom"
                                type="text"
                                value={form.prenom}
                                onChange={handleChange('prenom')}
                                error={errors.prenom}
                            />
                        </Grid>
                        {!isVerifiedTrainer && (
                            <>
                                <Grid item xs={12} md={6}>
                                    <FormDate
                                        label="Date de naissance"
                                        callback={handleDateNaissance}
                                        defaultValue={form.dateNaissance}
                                        required
                                        error={errors.dateNaissance}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="Ville de naissance"
                                        type="text"
                                        placeholder="Ex : Paris"
                                        value={form.villeNaissance}
                                        onChange={handleChange('villeNaissance')}
                                        error={errors.villeNaissance}
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Adresse email"
                                type="email"
                                value={form.email}
                                onChange={handleChange('email')}
                                error={errors.email}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Téléphone"
                                type="tel"
                                placeholder="Ex : 06 12 34 56 78"
                                value={form.telephone}
                                onChange={handleChange('telephone')}
                                error={errors.telephone}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Code de parrainage (facultatif)"
                                type="text"
                                placeholder="Ex : REF123"
                                value={form.code}
                                onChange={handleChange('code')}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Grid>

            {/* Section adresse */}
            <Grid item xs={12}>
                <Box sx={sectionBoxSx}>
                    <Box sx={sectionHeaderSx}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: '#f0fdf4',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <LocationOnIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight="700" fontFamily={brandConfig.fontFamily} color="text.primary">
                                Adresse de l'établissement
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily={brandConfig.fontFamily}>
                                Adresse principale de votre activité professionnelle
                            </Typography>
                        </Box>
                    </Box>
                    <AddressDetailed value={addressFields} onChange={handleAddressChange} errors={addressErrors} />
                </Box>
            </Grid>

            {/* Boutons d'action */}
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, pt: 1 }}>
                    <ButtonField
                        label={isVerifiedTrainer ? "Soumettre ma demande d'adhésion" : 'Continuer'}
                        variant="contained"
                        className="w-full font-bold lg:w-auto"
                        onClick={handleClickContinue}
                        startIcon={<HowToRegIcon />}
                        loading={loading}
                    />
                </Box>
            </Grid>
            <DialogLayout
                open={confirmOpen}
                handleClose={() => setConfirmOpen(false)}
                title="Confirmer vos informations"
                text={`Êtes-vous sûr${teachr?.sex === "MAN" ? "" : "e"} de toutes les informations renseignées ? Une fois passé à l'étape suivante, il ne sera plus possible de revenir en arrière.`}
                cancel="Annuler"
                valid="Valider"
                handleSubmit={handleSubmit}
            />
        </Settings>
    );
};

export default CooperativeForm;
